import { Pencil, Trash2, AlertTriangle, Link as LinkIcon, Mail, Loader2 } from 'lucide-preact'
import type { OnlineEventSpeakerViewDto } from '@/api/webs'
import type { WebinarRoomHostLinkDto } from '@/api/webinarRoom'
import { PersonAvatar } from '@/components/common/Avatar'
import { CopyButton } from '@/components/common/CopyButton'
import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { hostLinkStatus } from '@/utils/webinarRoomSetup'

type SpeakerUi = OnlineEventSpeakerViewDto
interface SpeakerUiWithPic extends SpeakerUi { ProfilePicUrl?: string | null }

/** An OrbitalX-room speaker's host link (ORB-1086). It works in every stage, signed off included. */
export interface SpeakerHostLink {
  link: WebinarRoomHostLinkDto
  sending: boolean
  onEmail: () => void
}

function HostLinkSection({ name, hostLink }: { name: string; hostLink: SpeakerHostLink }) {
  const { link, sending, onEmail } = hostLink
  const status = hostLinkStatus(link, new Date())
  return (
    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 flex flex-col gap-2.5">
      <p className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
        <span aria-hidden="true" className={`w-2 h-2 rounded-full flex-none ${status.opened ? 'bg-green-500' : 'border-[1.5px] border-slate-400 dark:border-slate-500'}`} />
        {status.text}
      </p>
      <div className="flex flex-wrap gap-2">
        <CopyButton text={link.Url} label={`${name}’s host link`} variant="default" size="md" buttonText="Copy host link" />
        <button
          type="button"
          onClick={onEmail}
          disabled={sending}
          title={`Sends the link to ${link.Email}`}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {sending
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending</>
            : <><Mail className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" /> Email the link</>}
        </button>
      </div>
    </div>
  )
}

export function SpeakerCard({ speaker, notInBigMarker, onDelete, onEdit, hostLink }: { speaker: SpeakerUi; notInBigMarker?: boolean; onDelete?: (sp: SpeakerUi) => void; onEdit?: () => void; hostLink?: SpeakerHostLink }) {
  const { canEdit } = useWorkspaceCapabilities()
  const name = speaker.Name || 'Speaker'
  const title = speaker.Title || '\u2014'
  const company = speaker.CompanyName || '\u2014'
  const avatar = (speaker as SpeakerUiWithPic).ProfilePicUrl || null
  const bio = (speaker.Bio || '').trim()
  const speakerType = (speaker.SpeakerType || '').trim()
  const linkedIn = (speaker.LinkedInProfileUrl || '').trim()

  // Company is required at sign-off, so it gets its own line in the gate's words rather than a place in
  // the list. Keyed on CompanyName alone, as the gate is: an id with no name still fails (ORB-1025).
  const companyMissing = !(speaker.CompanyName || '').trim()
  const missingFields: string[] = []
  if (!speaker.Title) missingFields.push('Title')
  if (!bio) missingFields.push('Bio')

  const cardFrame = 'p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40'
  const details = (
    <>
      <PersonAvatar
        fullName={name}
        profilePicUrl={avatar}
        size="lg"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                {linkedIn ? (
                  <a href={linkedIn} target="_blank" rel="noopener noreferrer" className="hover:underline flex items-center gap-1">
                    {name}
                    <LinkIcon className="w-3 h-3 text-primary-500" />
                  </a>
                ) : name}
              </div>
              {speakerType && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800">{speakerType}</span>
              )}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{title}{company !== '\u2014' ? `, ${company}` : ''}</div>
            <div className="text-[11px] text-slate-400 dark:text-slate-500 break-all mt-0.5">{speaker.Email}</div>
            {companyMissing && (
              <div className="mt-1.5 flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                No company. Add one before sign-off.
              </div>
            )}
            {missingFields.length > 0 && (
              <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Missing: {missingFields.join(', ')}
              </div>
            )}
            {notInBigMarker && (
              // Sign-off couldn't add this speaker as a BigMarker presenter (ORB-962). Sign-off itself succeeded,
              // so without this the only trace was a log line.
              <div
                className="mt-1.5 ml-1 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800"
                title="BigMarker didn't accept this speaker at sign-off. Reopen the web and sign off again to retry."
              >
                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                Not in BigMarker
              </div>
            )}
          </div>
          <div className="flex items-start gap-1">
            {canEdit && onEdit && (
              <button type="button" onClick={onEdit} className="p-1 rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700" aria-label="Edit participant" title="Edit participant"><Pencil className="w-3 h-3" /></button>
            )}
            {canEdit && onDelete && (
              <button type="button" onClick={() => onDelete(speaker)} className="p-1 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" aria-label="Remove participant" title="Remove participant"><Trash2 className="w-3 h-3" /></button>
            )}
          </div>
        </div>
        {bio && <p className="mt-2 text-xs text-slate-700 dark:text-slate-300 whitespace-pre-line line-clamp-5">{bio}</p>}
      </div>
    </>
  )

  // Edit and Remove follow the web's lock; the host link does not, so it sits below the details.
  if (hostLink) {
    return (
      <div className={cardFrame}>
        <div className="flex items-start gap-3">{details}</div>
        <HostLinkSection name={name} hostLink={hostLink} />
      </div>
    )
  }
  return <div className={`flex items-start gap-3 ${cardFrame}`}>{details}</div>
}
